/**
 * Hand-authored Snapshot fixtures.
 *
 * MASCHINENBAU is building the real sim in parallel; this kit must be reviewable *today*.
 * Each fixture is a tiny kinematic fake that emits genuine contract Snapshots and SimEvents,
 * so every prefab and every animation can be seen, screenshot and judged without core/.
 *
 * **Contract v1 coordinates.** Fixtures are *authored* in the cabinet's own frame — that is
 * how you hand-write a peg field or a coin pile — but they *publish* WORLD positions, with
 * the cabinet's `origin` added, exactly as core does. Anything less would leave the
 * renderer's origin subtraction untested until the first real sim frame, and the failure
 * mode (a pile drawn sixteen units to the right of its own pusher) is invisible in a fixture
 * whose origin is conveniently zero.
 */

import type {
  MachineId, Snapshot, SimEvent, RunState, CabinetState, TokenState, Tag, Material, RuleLine,
} from '../../shared/contract';
import type { Kit } from './kit';
import { fxClaw } from './anim/effects';
import { Vector3 } from 'three';

// ---------------------------------------------------------------- helpers

let _seed = 1;
function rand(): number {
  _seed = (_seed * 1664525 + 1013904223) >>> 0;
  return _seed / 4294967296;
}
function reseed(s: number): void { _seed = s >>> 0 || 1; }

const MATS: Material[] = ['kupfer', 'silber', 'gold', 'obsidian'];

/**
 * Where each cabinet stands in the sim's one shared Rapier world (contract v1). Sixteen
 * units apart, which is the spacing core uses — far enough that a bug in the renderer's
 * `P − O` rebase draws the pile on top of the neighbouring machine instead of subtly off.
 */
const SIM_ORIGIN: Record<MachineId, [number, number, number]> = {
  pachinko: [0, 0, 0],
  muenzschieber: [16, 0, 0],
  bandit: [32, 0, 0],
  roulette: [48, 0, 0],
  greifer: [64, 0, 0],
  flipper: [84, 0, 0],
};

/** cabinet-frame position → the world position the contract actually carries */
function w(machine: MachineId, at: [number, number, number]): [number, number, number] {
  const o = SIM_ORIGIN[machine];
  return [at[0] + o[0], at[1] + o[1], at[2] + o[2]];
}

function tok(id: number, material: Material, pos: [number, number, number], tags: Tag[] = [], radius = 0.5): TokenState {
  return {
    id, material, faceValue: material === 'gold' ? 5 : material === 'silber' ? 2 : 1,
    radius, tags, pos, quat: [0, 0, 0, 1], vel: [0, 0, 0], asleep: false, age: 0, stuck: false,
  };
}

function rules(...lines: [string, RuleLine['source'], boolean][]): RuleLine[] {
  return lines.map(([text, source, discovered], i) => ({ text, sourceId: `r${i}`, source, discovered }));
}

function run(over: Partial<RunState> = {}): RunState {
  return {
    tick: 0, floor: 3, wunder: null, wunderCard: [], fotoHeld: null, phase: 'machine', muenzen: 1240, tickets: 86, glueck: 6, verdacht: 24,
    debtDue: 300, ticketQuota: 40, beutel: [],
    items: [{ id: 'zinseszins', name: 'Zinseszins', kind: 'charm' }],
    activeMachine: null, hausverbot: false, over: false, won: false, ...over,
  };
}

function cabinet(machine: MachineId, tokens: TokenState[], over: Partial<CabinetState> = {}): CabinetState {
  const origin = SIM_ORIGIN[machine];
  const topf = over.topf ?? 140;
  const topfMax = over.topfMax ?? 400;
  return {
    machine,
    origin,
    // authored in the cabinet's frame, published in world (v1) — never mutate the input,
    // fixtures hand the same token array back every frame
    tokens: tokens.map((t) => ({ ...t, pos: w(machine, t.pos) })),
    topf, topfMax, topf01: Math.min(1, topf / topfMax),
    straehne: 2, limit01: 0.3, broken: false, powered: true,
    hausregeln: rules(['Einsatz: 1 Marke', 'hausregel', true]),
    helpCard: [], limitLabel: 'Struktur', tilt01: 0.12, lockoutUntil: -1,
    buyIn: 5, stakeLevel: 1, extra: null, ...over,
  };
}

// ---------------------------------------------------------------- mini physics

interface P { id: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; m: Material; tags: Tag[]; r: number; age: number; spin: number; lie: boolean; sleep: boolean }

class Mini {
  ps: P[] = [];
  pegs: [number, number][] = [];
  constructor(public floorY: number, public halfW: number) { }
  add(p: Partial<P> & { x: number; y: number }): P {
    const q: P = {
      id: this.ps.length + 1, x: p.x, y: p.y, z: p.z ?? 0,
      vx: p.vx ?? 0, vy: p.vy ?? 0, vz: p.vz ?? 0,
      m: p.m ?? 'kupfer', tags: p.tags ?? [], r: p.r ?? 0.5, age: p.age ?? 0, spin: rand() * 6,
      lie: p.lie ?? false, sleep: p.sleep ?? false,
    };
    this.ps.push(q);
    return q;
  }
  step(dt: number, gravity = 34): { hits: number; scored: P[] } {
    let hits = 0;
    const scored: P[] = [];
    for (const p of this.ps) {
      p.age += dt;
      if (p.sleep) continue;
      p.vy -= gravity * dt;
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
      p.spin += dt * 5;
      for (const [px, py] of this.pegs) {
        const dx = p.x - px, dy = p.y - py;
        const d = Math.hypot(dx, dy);
        if (d < p.r + 0.22 && d > 0.0001) {
          const nx = dx / d, ny = dy / d;
          p.x = px + nx * (p.r + 0.22);
          p.y = py + ny * (p.r + 0.22);
          const dot = p.vx * nx + p.vy * ny;
          const bounce = p.tags.includes('Gummi') ? 0.92 : 0.42;
          p.vx = (p.vx - 2 * dot * nx) * bounce + (rand() - 0.5) * 2.5;
          p.vy = (p.vy - 2 * dot * ny) * bounce;
          hits++;
        }
      }
      if (Math.abs(p.x) > this.halfW) { p.x = Math.sign(p.x) * this.halfW; p.vx *= -0.5; }
      if (p.y < this.floorY) {
        p.y = this.floorY;
        p.vy *= -0.28;
        p.vx *= 0.72;
        if (Math.abs(p.vy) < 1.2) { p.vy = 0; scored.push(p); }
      }
    }
    return { hits, scored };
  }
  toTokens(): TokenState[] {
    const K = 0.70710678;
    return this.ps.map((p) => {
      const s = Math.sin(p.spin * 0.5), c = Math.cos(p.spin * 0.5);
      // a coin in a PILE lies flat (face up); a coin in FLIGHT tumbles on its edge.
      // Getting this wrong turns the pusher's hoard into a row of macarons.
      const q: [number, number, number, number] = p.lie
        ? [-K * c, K * s, K * s, K * c]
        : [s * 0.2, s * 0.9, 0, c];
      return {
        id: p.id, material: p.m, faceValue: 1, radius: p.r, tags: p.tags,
        pos: [p.x, p.y, p.z] as [number, number, number],
        quat: q,
        vel: [p.vx, p.vy, p.vz] as [number, number, number],
        asleep: false, age: p.age, stuck: false,
      };
    });
  }
}

// ---------------------------------------------------------------- fixture type

/**
 * Fixtures fake the room: they animate ONE cabinet, so they do not author the v1 aisle
 * fields. The player fills them in, which keeps `Snapshot` honest (every field the
 * renderer reads is really on the contract) without making every fixture author a
 * diorama it does not have.
 */
export type FixtureFrame = Omit<Snapshot, 'room' | 'spieler'>;

export interface Fixture {
  id: string;
  title: string;
  subtitle: string;
  machine: MachineId | null;
  duration: number;
  /** the moment the screenshot gate takes its picture */
  shotAt: number;
  /** optional extra shots for the gate */
  shots?: { at: number; name: string }[];
  build(): {
    frame(t: number, dt: number): FixtureFrame;
    events: { at: number; ev: SimEvent }[];
    start?(kit: Kit): void;
  };
  /** compose the shot yourself — the default is the cabinet dolly-zoom */
  camera?(kit: Kit): void;
}

// ================================================================ fixtures

/** 1 — PACHINKO: a real drop through a real peg field */
const fixPachinko: Fixture = {
  id: 'pachinko', title: 'KUGELREGEN', subtitle: 'Ein Wurf durch das Stiftfeld',
  machine: 'pachinko', duration: 12, shotAt: 4.5,
  shots: [{ at: 2.2, name: 'a' }, { at: 6.0, name: 'b' }],
  build() {
    reseed(1337);
    const fieldY = 1.6 + 26 * 0.60;
    const mini = new Mini(fieldY - 15.5 / 2 + 1.4, 4.9);
    for (let r = 0; r < 11; r++) {
      for (let c = 0; c < 10; c++) {
        const stagger = (r % 2) * 0.5;
        const x = (c + stagger - 4.5) * (10.4 / 10);
        const y = fieldY + 15.5 / 2 - 2.2 - r * ((15.5 - 3.4) / 11);
        if (Math.abs(x) > 4.7) continue;
        mini.pegs.push([x, y]);
      }
    }
    let next = 0;
    const events: { at: number; ev: SimEvent }[] = [];
    for (let i = 0; i < 9; i++) {
      events.push({ at: 0.35 + i * 0.55, ev: { t: 'drop', token: i + 1, machine: 'pachinko' } });
    }
    events.push({ at: 5.4, ev: { t: 'topfFeed', machine: 'pachinko', amount: 38 } });
    events.push({ at: 6.6, ev: { t: 'score', token: 3, pocket: 'TOPF', value: 90, at: w('pachinko', [0, 8, 3.6]), tags: ['Magnet'] } });   // v1: the burst is tinted by the combo
    events.push({ at: 7.4, ev: { t: 'payout', muenzen: 210, tickets: 12, machine: 'pachinko' } });

    const TAGS: Tag[][] = [[], ['Gummi'], ['Magnet'], ['Gold'], ['Geist'], ['Magnet'], ['Spalt'], ['Zins'], ['Fluch']];
    return {
      events,
      frame(t: number, dt: number): FixtureFrame {
        while (next < 9 && t > 0.35 + next * 0.55) {
          mini.add({
            x: -3.6 + rand() * 0.6, y: fieldY + 8.4, z: 4.15,
            vx: 2.2 + rand() * 1.4, vy: -1, m: MATS[next % 4], tags: TAGS[next], r: 0.5,
          });
          next++;
        }
        mini.step(dt);
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'pachinko' }),
          cabinet: cabinet('pachinko', mini.toTokens(), {
            topf: 140 + Math.min(1, t / 6) * 120, straehne: t > 6.6 ? 3 : 2,
            hausregeln: rules(
              ['Einsatz: 1 Marke je Wurf', 'hausregel', true],
              ['Mitteltasche speist DEN TOPF', 'hausregel', true],
              ['[Glücks]: Taschen 20% weiter', 'enchant', true],
              ['Magnetfalle an den Taschen', 'zinken', false],
            ),
          }),
          events: [],
        };
      },
    };
  },
};

/** 2 — MÜNZSCHIEBER: the pile that persists all run, and the linear pusher */
const fixSchieber: Fixture = {
  id: 'schieber', title: 'MÜNZSCHIEBER', subtitle: 'Der Haufen ist Ihr Konto',
  machine: 'muenzschieber', duration: 12, shotAt: 5.0,
  camera(kit) {
    const c = kit.cabinets.get('muenzschieber')!;
    const o = c.group.position;
    kit.rig.place(new Vector3(o.x + 9, 26, o.z + 40), new Vector3(o.x - 1, 17.5, o.z + 1), 34).snap();
  },
  build() {
    reseed(77);
    const shelfY = 17.0;
    const mini = new Mini(shelfY + 0.35, 8.0);
    // the pile: 150 coins fed in all run, lying flat, mounded toward the middle
    for (let i = 0; i < 170; i++) {
      const x = (rand() - 0.5) * 12.6;
      const z = -4.6 + rand() * 7.6;
      const centre = 1 - Math.min(1, Math.hypot(x / 6.6, (z + 0.6) / 4.0));
      const layers = Math.max(1, Math.round(1 + centre * 5.4));
      const layer = Math.floor(rand() * layers);
      mini.add({
        x, y: shelfY + 0.30 + layer * 0.19, z,
        m: MATS[Math.floor(rand() * 4)],
        tags: rand() < 0.12 ? ['Zins'] : rand() < 0.06 ? ['Gold'] : [],
        age: rand() * 40, lie: true, sleep: true,
      });
    }
    const events: { at: number; ev: SimEvent }[] = [
      { at: 1.2, ev: { t: 'drop', token: 200, machine: 'muenzschieber' } },
      { at: 3.0, ev: { t: 'zinsTick', token: 12, newValue: 8 } },
      { at: 4.4, ev: { t: 'topfFeed', machine: 'muenzschieber', amount: 24 } },
      { at: 6.2, ev: { t: 'payout', muenzen: 340, tickets: 6, machine: 'muenzschieber' } },
      { at: 8.5, ev: { t: 'nachlegen', machine: 'muenzschieber', newStakes: 4 } },
      // --- contract v1, all after shotAt so the committed gate image is unchanged ---
      { at: 6.6, ev: { t: 'payoutStage', stage: 'straehne', before: 340, after: 680, machine: 'muenzschieber' } },
      { at: 7.1, ev: { t: 'straehne', machine: 'muenzschieber', from: 2, to: 3 } },   // ignite
      { at: 7.6, ev: { t: 'sediment', token: 12, packed: true } },
      { at: 9.4, ev: { t: 'straehne', machine: 'muenzschieber', from: 3, to: 1 } },   // snuff
      { at: 10.2, ev: { t: 'tiltLockout', machine: 'muenzschieber', until: 10.2 * 120 + 360 } },
    ];
    let dropped = false;
    return {
      events,
      start(kit) { (kit.cabinets.get('muenzschieber')!.api as { setSpeed(v: number): void }).setSpeed(1); },
      frame(t: number, dt: number): FixtureFrame {
        if (!dropped && t > 1.2) {
          dropped = true;
          for (let i = 0; i < 6; i++) {
            mini.add({ x: (rand() - 0.5) * 9, y: shelfY + 8, z: 3.2, vy: -2, m: 'gold', tags: ['Zins'] });
          }
        }
        // the pusher shoves the pile forward, linearly. The House does not emote.
        const push = Math.sin(t * 1.35) * 0.5 + 0.5;
        for (const p of mini.ps) {
          if (p.z < -2.4 + push * 3.4 + 1.6) p.z += dt * 1.05;
          if (p.z > 4.6 && p.sleep) { p.sleep = false; p.lie = false; p.vz = 2.2; p.vy = -1; }
        }
        mini.step(dt, 30);
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'muenzschieber' }),
          cabinet: cabinet('muenzschieber', mini.toTokens(), {
            topf: 90 + t * 12,
            hausregeln: rules(
              ['Der Haufen bleibt über Nacht', 'hausregel', true],
              ['Seitlicher Abgang: Haus kassiert', 'hausregel', true],
              ['Zinsmünzen reifen im Haufen', 'enchant', true],
            ),
          }),
          events: [],
        };
      },
    };
  },
};

/** 3 — BANDIT: lever, three drums, three detents */
const fixBandit: Fixture = {
  id: 'bandit', title: 'EINARMIGER BANDIT', subtitle: 'Ihr Beutel ist der Walzenkranz',
  machine: 'bandit', duration: 11, shotAt: 3.4,
  build() {
    reseed(9);
    const events: { at: number; ev: SimEvent }[] = [
      { at: 2.6, ev: { t: 'reelStop', reel: 0, symbolToken: 2 } },
      { at: 3.3, ev: { t: 'reelStop', reel: 1, symbolToken: 2 } },
      { at: 4.2, ev: { t: 'reelStop', reel: 2, symbolToken: 2 } },
      { at: 4.8, ev: { t: 'jackpot', machine: 'bandit', value: 1800 } },
      { at: 6.4, ev: { t: 'payout', muenzen: 1800, tickets: 40, machine: 'bandit' } },
      // v1 `bankBreak.mode`: on a Bandit the drums leave their axles, they do not shatter
      { at: 8.0, ev: { t: 'bankBreak', machine: 'bandit', payout: 9400, mode: 'axle' } },
      { at: 9.8, ev: { t: 'zinkenStolen', machine: 'bandit', zinken: 'Gezinkte Walze' } },
    ];
    const tray: TokenState[] = [];
    for (let i = 0; i < 12; i++) {
      tray.push(tok(i + 1, MATS[i % 4], [(rand() - 0.5) * 5, 3.2 + rand() * 0.4, 5.4 + rand()], i % 5 === 0 ? ['Spalt'] : []));
    }
    return {
      events,
      start(kit) {
        const api = kit.cabinets.get('bandit')!.api as { spinReel(i: number): void; pullLever(v: number): void };
        for (let i = 0; i < 3; i++) api.spinReel(i);
      },
      frame(t: number): FixtureFrame {
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'bandit' }),
          cabinet: cabinet('bandit', tray, {
            topf: 260, straehne: t > 4.8 ? 4 : 2, limit01: t > 4.8 ? 0.9 : 0.5,
            hausregeln: rules(
              ['Walzen ziehen aus DEM BEUTEL', 'hausregel', true],
              ['[Spalt] zählt als zwei Symbole', 'enchant', true],
              ['[Geist] darf zwischen Rasten halten', 'enchant', true],
              ['Gezinkte Walze: dritte Rolle', 'zinken', true],
            ),
          }),
          events: [],
        };
      },
    };
  },
};

/** 4 — ROULETTE: your token IS the ball */
const fixRoulette: Fixture = {
  id: 'roulette', title: 'ROULETTE', subtitle: 'Eine Ihrer Marken wird zur Kugel',
  machine: 'roulette', duration: 12, shotAt: 3.0,
  build() {
    reseed(21);
    const felt = 11.35;
    const bets: TokenState[] = [];
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      const r = 6.2 + rand() * 1.6;
      bets.push(tok(i + 1, MATS[i % 4], [Math.cos(a) * r, felt + 0.25, Math.sin(a) * r],
        i % 4 === 0 ? ['Magnet'] : i % 7 === 0 ? ['Zins'] : []));
    }
    const events: { at: number; ev: SimEvent }[] = [
      { at: 5.2, ev: { t: 'wheelSettle', pocket: 17, token: 3 } },
      { at: 6.4, ev: { t: 'score', token: 3, pocket: '17', value: 340, at: w('roulette', [0, 12.5, 0]) , tags: [] } },
      { at: 7.0, ev: { t: 'payout', muenzen: 620, tickets: 8, machine: 'roulette' } },
      { at: 9.0, ev: { t: 'verdacht', delta: 9, reason: 'Gewinnserie über Erwartung' } },
      { at: 10.6, ev: { t: 'bankBreak', machine: 'roulette', payout: 7100, mode: 'wheel' } },
    ];
    return {
      events,
      start(kit) { (kit.cabinets.get('roulette')!.api as { launch(p: number): void }).launch(1.15); },
      frame(t: number): FixtureFrame {
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'roulette', verdacht: t > 9 ? 33 : 24 }),
          cabinet: cabinet('roulette', bets, {
            topf: 210,
            hausregeln: rules(
              ['Eine Marke wird zur Kugel', 'hausregel', true],
              ['Einsätze bleiben liegen (Zins)', 'hausregel', true],
              ['[Magnet]: Kugel sucht Ihre Felder', 'enchant', true],
              ['Gewichtete Kugel', 'zinken', false],
            ),
          }),
          events: [],
        };
      },
    };
  },
};

/** 5 — GREIFER: the shop, and the bear that does not fit */
const fixGreifer: Fixture = {
  id: 'greifer', title: 'PREISTHEKE', subtitle: 'Der Bär passt nicht durch den Schacht',
  machine: 'greifer', duration: 13, shotAt: 6.0,
  camera(kit) {
    const c = kit.cabinets.get('greifer')!;
    const o = c.group.position;
    kit.rig.place(new Vector3(o.x + 13, 24, o.z + 30), new Vector3(o.x, 20, o.z + 1), 32).snap();
  },
  build() {
    reseed(55);
    const inside: TokenState[] = [];
    for (let i = 0; i < 10; i++) {
      inside.push(tok(i + 1, MATS[i % 4], [(rand() - 0.5) * 10, 14.4, (rand() - 0.5) * 8],
        i % 3 === 0 ? ['Gluecks'] : []));
    }
    const events: { at: number; ev: SimEvent }[] = [
      { at: 8.6, ev: { t: 'verdacht', delta: 4, reason: 'Griff am Glas' } },
      { at: 10.0, ev: { t: 'payout', muenzen: 0, tickets: 25, machine: 'greifer' } },
      { at: 11.0, ev: { t: 'grab', prize: 'Plüschbär', kept: false } },
      { at: 11.9, ev: { t: 'item', id: 'brecheisen', name: 'Brecheisen', gained: true } },
    ];
    return {
      events,
      start(kit) {
        // the claw run: reach, drop, grip, and lose it — Müder Greifer is installed
        kit.anim.play(fxClaw(kit.ctx, 3.6, -2.2, false));
      },
      frame(t: number): FixtureFrame {
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'greifer', tickets: 86 - Math.floor(t) }),
          cabinet: cabinet('greifer', inside, {
            topf: 60,
            hausregeln: rules(
              ['Ein Griff je Marke', 'hausregel', true],
              ['Doppelt oder fallen lassen', 'hausregel', true],
              ['Der Bär passt nicht durch den Schacht', 'hausregel', true],
              ['Müder Greifer', 'zinken', true],
            ),
          }),
          events: [],
        };
      },
    };
  },
};

/** 6 — PAYOUT STORM: the pipeline running hot, four machines at once */
const fixStorm: Fixture = {
  id: 'storm', title: 'MÜNZREGEN', subtitle: 'Die Auszahlungskette läuft heiß',
  machine: 'pachinko', duration: 12, shotAt: 3.2,
  shots: [{ at: 5.4, name: 'b' }],
  build() {
    reseed(404);
    const fieldY = 1.6 + 26 * 0.60;
    const mini = new Mini(fieldY - 15.5 / 2 + 1.4, 4.9);
    for (let i = 0; i < 26; i++) {
      mini.add({
        x: (rand() - 0.5) * 8, y: fieldY + rand() * 7, z: 4.15,
        vx: (rand() - 0.5) * 6, vy: -rand() * 3,
        m: MATS[Math.floor(rand() * 4)],
        tags: [(['Magnet', 'Gold', 'Gummi', 'Echo', 'Feuer', 'Zins'] as Tag[])[Math.floor(rand() * 6)]],
      });
    }
    const events: { at: number; ev: SimEvent }[] = [];
    for (let i = 0; i < 7; i++) {
      events.push({ at: 0.6 + i * 1.2, ev: { t: 'payout', muenzen: 180 + i * 90, tickets: 6 + i * 3, machine: 'pachinko' } });
      events.push({ at: 1.0 + i * 1.2, ev: { t: 'topfFeed', machine: 'pachinko', amount: 18 } });
      events.push({ at: 1.4 + i * 1.2, ev: { t: 'score', token: i + 1, pocket: '50', value: 120, at: w('pachinko', [0, 9, 3.6]) , tags: [] } });
    }
    events.push({ at: 6.0, ev: { t: 'glueckSpent', stream: 'peg-bounce', event: 'peg' } });
    return {
      events,
      frame(t: number, dt: number): FixtureFrame {
        mini.step(dt);
        for (const p of mini.ps) {
          if (p.y <= mini.floorY + 0.05) { p.y = fieldY + 8; p.vy = -1; p.x = (rand() - 0.5) * 8; }
        }
        return {
          tick: Math.round(t * 120),
          run: run({ activeMachine: 'pachinko', muenzen: 1240 + Math.floor(t * 300) }),
          cabinet: cabinet('pachinko', mini.toTokens(), { topf: 120 + t * 30, straehne: 5, limit01: 0.7 }),
          events: [],
        };
      },
    };
  },
};

/** 7 — BANK SPRENGEN: the money shot */
const fixBurst: Fixture = {
  id: 'burst', title: 'BANK GESPRENGT', subtitle: 'Das Glas hält der Auszahlung nicht stand',
  machine: 'muenzschieber', duration: 14, shotAt: 3.9,
  shots: [{ at: 5.2, name: 'b' }, { at: 7.5, name: 'c' }],
  build() {
    reseed(999);
    const shelfY = 17.0;
    const mini = new Mini(shelfY + 0.35, 8.0);
    for (let i = 0; i < 190; i++) {
      const x = (rand() - 0.5) * 12.6, z = -4.6 + rand() * 7.8;
      const centre = 1 - Math.min(1, Math.hypot(x / 6.6, (z + 0.6) / 4.0));
      mini.add({
        x, y: shelfY + 0.30 + Math.floor(rand() * Math.max(1, 1 + centre * 5)) * 0.19,
        z, m: MATS[Math.floor(rand() * 4)],
        tags: rand() < 0.2 ? ['Zins'] : [], age: rand() * 60, lie: true, sleep: true,
      });
    }
    const events: { at: number; ev: SimEvent }[] = [
      { at: 1.0, ev: { t: 'topfFeed', machine: 'muenzschieber', amount: 120 } },
      { at: 2.0, ev: { t: 'bankBreak', machine: 'muenzschieber', payout: 12480, mode: 'glass' } },
      { at: 8.5, ev: { t: 'verdacht', delta: 22, reason: 'Sie haben die Möbel zerlegt' } },
      { at: 11.0, ev: { t: 'payout', muenzen: 12480, tickets: 220, machine: 'muenzschieber' } },
    ];
    return {
      events,
      frame(t: number, dt: number): FixtureFrame {
        if (t > 2.05 && t < 2.6) {
          // the glass goes and the whole bank leaves the building
          for (const p of mini.ps) {
            p.sleep = false; p.lie = false;
            p.vy += 15 * dt; p.vx += (rand() - 0.5) * 32 * dt; p.vz += 24 * dt;
          }
        }
        mini.step(dt, 30);
        return {
          tick: Math.round(t * 120),
          run: run({ activeMachine: 'muenzschieber', verdacht: t > 8.5 ? 46 : 24 }),
          cabinet: cabinet('muenzschieber', mini.toTokens(), {
            topf: t > 2 ? 8 : 390, limit01: t > 2 ? 1 : 0.96, straehne: 5,
          }),
          events: [],
        };
      },
    };
  },
};

/** 8 — DER KASSENBON: the run prints from a till roll */
const fixReceipt: Fixture = {
  id: 'receipt', title: 'KASSENBON', subtitle: 'Der Abend, ausgedruckt',
  machine: 'bandit', duration: 16, shotAt: 7.5,
  shots: [{ at: 10.5, name: 'b' }],
  camera(kit) {
    const c = kit.cabinets.get('bandit')!;
    const o = c.group.position;
    kit.rig.place(new Vector3(o.x - 4.5, 21, o.z + 33), new Vector3(o.x - 1.5, 18, o.z + 6), 36).snap();
  },
  build() {
    const lines = [
      '*SPIELHÖLLE',
      'RASTSTÄTTE NORD · A7',
      '=',
      'ETAGE 1|50 MZ',
      'ETAGE 2|120 MZ',
      'ETAGE 3|300 MZ',
      'ETAGE 4|700 MZ',
      '=',
      '*BESTE KOMBINATION',
      'GEISTER-MAGNET-KASKADE',
      '4.812 MÜNZEN',
      '=',
      'DÜMMSTER VERLUST',
      'Bär im Schacht|-900 MZ',
      'VERDACHT MAX|71',
      '=',
      'EINBEHALTEN',
      'Zinsmünze (x2)',
      'Magnetschuh',
      '=',
      '*NICHT BEZAHLT',
      'DER CROUPIER DANKT',
    ];
    const events: { at: number; ev: SimEvent }[] = [
      { at: 0.8, ev: { t: 'abrechnung', due: 700, paid: 430, confiscated: ['Zinsmünze', 'Magnetschuh'] } },
      { at: 5.0, ev: { t: 'receipt', lines } },
    ];
    const tray: TokenState[] = [];
    for (let i = 0; i < 5; i++) tray.push(tok(i + 1, 'kupfer', [(i - 2) * 1.3, 3.2, 5.6]));
    return {
      events,
      frame(t: number): FixtureFrame {
        return {
          tick: Math.round(t * 120),
          run: run({ activeMachine: 'bandit', muenzen: 430, floor: 4, debtDue: 700 }),
          cabinet: cabinet('bandit', tray, { broken: t > 3, topf: 0 }),
          events: [],
        };
      },
    };
  },
};

/** 9 — DIE MARKEN: the tag catalogue, every enchantment side by side */
const fixTokens: Fixture = {
  id: 'tokens', title: 'DER BEUTEL', subtitle: 'Material × Verzauberung — alle zwölf Tags',
  machine: 'roulette', duration: 14, shotAt: 3.0,
  camera(kit) {
    const c = kit.cabinets.get('roulette')!;
    const o = c.group.position;
    kit.rig.place(new Vector3(o.x, 17.6, o.z + 19.5), new Vector3(o.x, 11.9, o.z + 6.8), 32).snap();
  },
  build() {
    const sets: Tag[][] = [
      [], ['Magnet'], ['Geist'], ['Gummi'], ['Kleber'], ['Spalt'],
      ['Gold'], ['Fluch'], ['Feuer'], ['Gluecks'], ['Schwer'], ['Zins'],
      ['Echo'], ['Geist', 'Magnet'], ['Gold', 'Fluch'], ['Magnet', 'Zins', 'Spalt'],
    ];
    // laid out on the near edge of the felt, tilted toward the camera so the FACES read
    const felt = 11.35;
    const list: TokenState[] = sets.map((tags, i) => {
      const col = i % 6, row = Math.floor(i / 6);
      const t = tok(i + 1, MATS[i % 4], [(col - 2.5) * 1.75, felt + 0.62, 8.6 - row * 1.7], tags);
      t.age = 7;
      t.quat = [-0.38, 0, 0, 0.92];
      return t;
    });
    return {
      events: [],
      frame(t: number): FixtureFrame {
        for (const tk of list) tk.age = 7 + (t % 10);
        return {
          tick: Math.round(t * 120), run: run({ activeMachine: 'roulette' }),
          cabinet: cabinet('roulette', list, { topf: 180 }),
          events: [],
        };
      },
    };
  },
};

/** 10 — DIE HALLE: the establishing shot, and the sign gag */
const fixRoom: Fixture = {
  id: 'room', title: 'SPIELHÖLLE', subtitle: 'Raststätte nach Mitternacht',
  machine: null, duration: 18, shotAt: 5.0,
  shots: [{ at: 11.0, name: 'b' }],
  camera(kit) {
    // the establishing shot: sign high and centred, the aisle receding into the dark
    kit.rig.place(new Vector3(16, 20.5, 44), new Vector3(-4, 17.0, -20), 42).snap();
  },
  build() {
    const events: { at: number; ev: SimEvent }[] = [
      { at: 8.0, ev: { t: 'floorStart', floor: 3 } },
      { at: 14.0, ev: { t: 'verdacht', delta: 12, reason: 'Der Croupier sieht her' } },
      { at: 15.6, ev: { t: 'quotaMet', floor: 3, tickets: 40 } },
      { at: 16.6, ev: { t: 'floorEnd', floor: 3 } },
    ];
    return {
      events,
      start() { },
      frame(t: number): FixtureFrame {
        return {
          tick: Math.round(t * 120),
          run: run({ activeMachine: null, floor: 3 }),
          cabinet: null,
          events: [],
        };
      },
    };
  },
};

export const FIXTURES: Fixture[] = [
  fixRoom, fixPachinko, fixSchieber, fixBandit, fixRoulette, fixGreifer,
  fixTokens, fixStorm, fixBurst, fixReceipt,
];

// ================================================================ player

export class FixturePlayer {
  current: Fixture | null = null;
  t = 0;
  private inst: ReturnType<Fixture['build']> | null = null;
  private fired = new Set<number>();
  private kit: Kit;
  looping = true;

  constructor(kit: Kit) { this.kit = kit; }

  list(): Fixture[] { return FIXTURES; }

  play(id: string): Fixture | null {
    const f = FIXTURES.find((x) => x.id === id);
    if (!f) return null;
    this.current = f;
    this.inst = f.build();
    this.t = 0;
    this.fired.clear();
    this.kit.anim.clear();
    this.kit.coins.clear();
    this.kit.ctx.setVignette?.(0);
    this.kit.stage.timescale = 1;
    this.kit.focus(f.machine);
    f.camera?.(this.kit);
    this.kit.say(f.title, f.subtitle);
    this.inst.start?.(this.kit);
    return f;
  }

  /** deterministic seek, used by the screenshot gates */
  seek(id: string, seconds: number, dt = 1 / 60): void {
    this.play(id);
    const steps = Math.max(1, Math.round(seconds / dt));
    for (let i = 0; i < steps; i++) {
      this.update(dt);
      this.kit.step(dt);
    }
  }

  update(dt: number): void {
    if (!this.current || !this.inst) return;
    this.t += dt;
    for (let i = 0; i < this.inst.events.length; i++) {
      const e = this.inst.events[i];
      if (!this.fired.has(i) && this.t >= e.at) {
        this.fired.add(i);
        this.kit.pushSnapshot(full({ ...this.inst.frame(this.t, 0), events: [e.ev] }));
      }
    }
    this.kit.pushSnapshot(full(this.inst.frame(this.t, dt)));
    if (this.t > this.current.duration) {
      if (this.looping) this.play(this.current.id);
      else this.current = null;
    }
  }
}

/** Lift a fixture frame to a full contract-v1 Snapshot: one cabinet, standing alone. */
function full(f: FixtureFrame): Snapshot {
  const c = f.cabinet;
  return {
    ...f,
    room: c
      ? [{
          machine: c.machine, origin: c.origin, topf: c.topf, topf01: c.topf01,
          broken: c.broken, powered: c.powered, tokenCount: c.tokens.length, suspect: false,
        }]
      : [],
    spieler: [0, 0, 8],
  };
}
